import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { adminMenuConfig, customerMenuConfig, type MenuItem } from '@/config/menu';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

export function useMenu() {
  const route = useRoute();
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();

  const hasPermission = (permission: string): boolean => {
    const user = authStore.currentUser;
    if (!user) {
      return false;
    }

    return user.permissions?.includes(permission) ?? false;
  };

  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .map(item => {
        const filteredItem = { ...item } as MenuItem & { items?: MenuItem[] };

        if (filteredItem.children && filteredItem.children.length > 0) {
          const filteredChildren = filterMenuItems(filteredItem.children);

          if (filteredChildren.length > 0) {
            filteredItem.items = filteredChildren;
          }
          delete filteredItem.children;
        }

        return filteredItem;
      })
      .filter(item => {
        if (item.hideFromMenu) {
          return false;
        }

        if (item.permission) {
          return hasPermission(item.permission);
        }

        if (item.to) {
          return true;
        }

        if (item.items && item.items.length > 0) {
          return true;
        }

        return false;
      });
  };

  const addCompanyParams = (items: MenuItem[]): MenuItem[] => {
    const companyId = companyStore.selectedCompanyId || (route.params.companyId as string);

    if (!companyId) {
      return items;
    }

    return items.map(item => {
      const updatedItem = { ...item };

      if (updatedItem.to && typeof updatedItem.to === 'object' && 'name' in updatedItem.to) {
        updatedItem.to = {
          ...updatedItem.to,
          params: { companyId },
        };
      }

      if (updatedItem.children) {
        updatedItem.children = addCompanyParams(updatedItem.children);
      }

      return updatedItem;
    });
  };

  const visibleMenuItems = computed(() => {
    if (!authStore.currentUser) {
      return [];
    }

    let menuItems: MenuItem[] = [];

    if (authStore.currentContext === 'employee') {
      menuItems = adminMenuConfig;
    } else if (authStore.currentContext === 'customer') {
      const companyId = companyStore.selectedCompanyId || (route.params.companyId as string);
      if (!companyId) {
        return [];
      }

      menuItems = addCompanyParams(customerMenuConfig);
    }

    return filterMenuItems(menuItems);
  });

  const canAccessRoute = (permission?: string): boolean => {
    if (!permission) {
      return true;
    }

    return hasPermission(permission);
  };

  const findMenuItemByRouteName = (routeName: string, items?: MenuItem[]): MenuItem | undefined => {
    if (!items) {
      if (authStore.currentContext === 'employee') {
        items = adminMenuConfig;
      } else if (authStore.currentContext === 'customer') {
        items = customerMenuConfig;
      } else {
        return undefined;
      }
    }

    for (const item of items) {
      if (item.to && typeof item.to === 'object' && 'name' in item.to && item.to.name === routeName) {
        return item;
      }

      if (item.children) {
        const found = findMenuItemByRouteName(routeName, item.children);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  };

  const getAllMenuPermissions = (items?: MenuItem[]): string[] => {
    if (!items) {
      items = authStore.currentContext === 'employee' ? adminMenuConfig : customerMenuConfig;
    }

    const permissions: string[] = [];

    items.forEach(item => {
      if (item.permission) {
        permissions.push(item.permission);
      }

      if (item.children) {
        permissions.push(...getAllMenuPermissions(item.children));
      }
    });

    return [...new Set(permissions)];
  };

  return {
    visibleMenuItems,
    hasPermission,
    canAccessRoute,
    findMenuItemByRouteName,
    getAllMenuPermissions,
  };
}
